import {Form, Select, TimePicker} from 'antd';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import React, {memo} from 'react';

import {Client, User} from '../../../../types/src';
import {CONSTANTS, STRINGS} from '../../utils';
import {InfoIcon} from '../icons';
import PgTimezones from '../../../../../components/header/components/AccountSettings/components/pg_timezone_names';

const {Option} = Select;

const DEFAULT_TIMEZONE = 'UTC';
const TIME_FORMAT = 'HH:mm:ss';
const START_TIME_MAX_HOUR = 14;
const START_TIME_ERROR = 'To complete the run during the day, the time must be set before 2pm';
const disabledCampaignStartTime = () => ({
  disabledHours: () => Array.from({length: 24 - START_TIME_MAX_HOUR}, (_, index) => index + START_TIME_MAX_HOUR),
});

interface UserCampaignScheduleFieldsProps {
  timezone?: string;
  campaigns_start_time?: string | null;
  error?: any;
  touched?: any;
  onTimezoneChange: (value: string) => void;
  onStartTimeChange: (time: dayjs.Dayjs | null, timeString: string) => void;
}

export const getUserCampaignScheduleInitialValues = (user: User, client: Client) => ({
  campaigns_start_time: user.campaigns_start_time ?? client.campaigns_start_time ?? undefined,
  timezone: user.timezone || client.timezone || DEFAULT_TIMEZONE,
});

export const shouldShowUserCampaignScheduleFields = (client?: Client | null, user?: User | null) =>
  !!client?.k8s_support || !!user?.k8s_support;

export const getUserCampaignScheduleValidationError = (campaignsStartTime?: string | null) => {
  if (!campaignsStartTime) {
    return undefined;
  }

  const hours = moment(campaignsStartTime, CONSTANTS.main_date_format).toDate().getHours();
  return hours < START_TIME_MAX_HOUR ? undefined : START_TIME_ERROR;
};

export const UserCampaignScheduleFields = memo(
  ({
    timezone,
    campaigns_start_time,
    error,
    touched,
    onTimezoneChange,
    onStartTimeChange,
  }: UserCampaignScheduleFieldsProps) => {
    return (
      <div className="userCampaignScheduleFields">
        <Form.Item
          className="userCampaignScheduleFieldsStartTime"
          help={error && touched ? error : ''}
          validateStatus={error && touched ? 'error' : ''}
          label={
            <span>
              {'Start campaigns time: '}
              <InfoIcon message={STRINGS.info_start_campaign_time} />
            </span>
          }
        >
          <TimePicker
            allowClear={true}
            onChange={onStartTimeChange}
            disabledTime={disabledCampaignStartTime}
            value={
              campaigns_start_time
                ? dayjs(moment(campaigns_start_time, CONSTANTS.main_date_format).toDate())
                : undefined
            }
            defaultOpenValue={dayjs('00:00:00', TIME_FORMAT)}
          />
        </Form.Item>
        <Form.Item className="userCampaignScheduleFieldsTimezone" label="Timezone:">
          <Select
            showSearch
            placeholder="Select a timezone"
            value={timezone}
            onChange={onTimezoneChange}
            optionFilterProp="children"
            filterOption={(input: any, option: any) =>
              `${option.props.children}`.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            getPopupContainer={trigger => trigger.parentElement}
          >
            {PgTimezones.map(timezoneOption => (
              <Option key={timezoneOption} value={timezoneOption}>
                {`${timezoneOption} (GMT${moment.tz(timezoneOption).format('Z')})`}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </div>
    );
  }
);
